import React, {useState, useRef, useEffect} from "react";
import {ToastContainer, toast} from "react-toastify";
import Translate from "../../../Components/Multilanguage/Translate";
import GiphySearchbox from 'react-giphy-searchbox';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import Gif from '@mui/icons-material/Gif';
import CloseIcon from '@mui/icons-material/Close';
import {commentGifUploadThumbnail, commentImageUploadThumbnail, createComment, editComment} from "../service";
import {useTranslateText} from "../../../Components/Multilanguage/Translate";
import {useSelector} from "react-redux";
import styles from './scss/_postcomment.module.scss';
import {createTurndown, formatMarkdownToHtml} from "../../VideoPlayer/utils/helper";

const turndown = createTurndown();

const PostComment = ({ postId, id = null, projectInfo, parentCommentId = null, onCommentPosted, initialContent = "", isEditComment = false, hideMedia = false }) => {
    const userDetails = useSelector((state) => state?.userDetails?.value);
    const accessToken = useSelector((state) => state?.accessToken?.value);
    const [commentInputValue, setCommentInputValue] = useState(""); // 👈 now a plain string, not HTML
    const [selectedGif, setSelectedGif] = useState(null);
    const [imageDimensions, setImageDimensions] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [isGiphyModalOpen, setIsGiphyModalOpen] = useState(false);
    const appInfo = { projectDetails: projectInfo, accessToken };
    const gifApiKey = projectInfo?.projectConfig?.config?.GIPHY_API_KEY;
    const theme = projectInfo?.projectConfig?.config?.THEME;
    const fileInputRef = useRef(null);
    const translateText = useTranslateText();
    const fileInputId = parentCommentId ? `file-reply-${parentCommentId}` : `file-main-${postId}`;

    useEffect(() => {
        // 👇 since the input is plain text now, strip markdown/HTML down to plain text for editing
        const formattedContent = initialContent ? formatMarkdownToHtml(initialContent).replace(/<[^>]+>/g, '') : "";
        setCommentInputValue(formattedContent);
    }, [initialContent]);

    const onGifSelect = async (item) => {
        const gifUrl = item?.images?.fixed_width_small?.url;
        const response = await commentGifUploadThumbnail(gifUrl, appInfo);
        if (response.data.data) {
            setSelectedGif(item);
            setIsGiphyModalOpen(false);
        }
    };

    const handleFileChange = async (e) => {
        var allowedExtensions = /(\.jpg|\.jpeg|\.png|\.gif)$/i;
        if (!allowedExtensions.exec(e?.target?.files[0]?.name)) {
            alert(<Translate textKey={'invalid_extension_message'} />);
        } else {
            setSelectedGif(null);
            const files = e?.target?.files;
            const response = await commentImageUploadThumbnail(files[0], appInfo);
            if (response.data.data) {
                const reader = new FileReader();
                reader.onloadend = () => {
                    const img = new window.Image();
                    img.onload = () => {
                        setImageDimensions({
                            width: parseInt(img.width), height: parseInt(img.height), url: response.data.data
                        });
                    };
                    img.src = reader.result;
                    setImagePreview(reader.result);
                };
                reader.readAsDataURL(files[0]);
                if (fileInputRef.current) {
                    fileInputRef.current.value = null;
                }
            }
        }
    };

    const handleRemoveSelection = () => {
        setSelectedGif(null);
        setImagePreview(null);
    };

    const postCommentHandler = async (post_id) => {
        if (commentInputValue || selectedGif || imageDimensions) {
            const markdownContent = commentInputValue; // 👈 plain text now, no turndown needed
            let comment_data = {
                content: markdownContent || '',
                post_id: Number(post_id),
            };
            if (selectedGif) {
                comment_data['gif[height]'] = selectedGif.images.fixed_width_small.height;
                comment_data['gif[width]'] = selectedGif.images.fixed_width_small.width;
                comment_data['gif[url]'] = selectedGif.images.fixed_width_small.url;
            }
            if (imageDimensions) {
                comment_data['image[height]'] = imageDimensions.height;
                comment_data['image[width]'] = imageDimensions.width;
                comment_data['image[url]'] = imageDimensions.url;
            }
            if (parentCommentId) {
                comment_data.comment_id = parentCommentId;
            }
            try {
                const res = isEditComment
                    ? await editComment(appInfo, id ?? parentCommentId, comment_data)
                    : await createComment(appInfo, comment_data);
                resetLocalState();
                if (onCommentPosted) onCommentPosted(res, commentInputValue);
                toast.success(<Translate textKey={'comment_posted'} />, { position: 'top-center' }); // 👈 added simple success toast
            } catch (err) {
                console.log(err);
                toast.error(<Translate textKey={'comment_or_image_required'} />, { position: 'top-center' });
            }
        } else {
            toast.error(<Translate textKey={'comment_or_image_required'} />, { position: 'top-center' });
        }
    };

    function resetLocalState() {
        setCommentInputValue('');
        setSelectedGif(null);
        setImagePreview(null);
        setImageDimensions(null);
    }

    const handleKeyDown = (e) => {
        // 👇 optional: submit on Enter, like a chat input
        if (e.key === 'Enter') {
            postCommentHandler(postId);
        }
    };

    return (
        <div className={`${styles.textContainer} composer member`}>
            <ToastContainer />
            <div className={`${styles.imageContainer} av`}>
                {
                    userDetails?.user_image ? (
                        <img className="profilepic" src={userDetails?.user_image} alt="Profile" />
                    ) : (<span className="nameAvatar">{userDetails?.first_name?.slice(0, 1)?.toUpperCase()}</span>)
                }
            </div>

            <input
                type="text"
                value={commentInputValue}
                onChange={(e) => setCommentInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={translateText("add_your_comment")}
                className={styles.inputContainer}
            />

            {/* {!hideMedia && (
                <div className={styles.buttonContainer} style={{ display: 'flex' }}>
                    <label htmlFor={fileInputId}
                           className={`${styles.customFileUpload} ${theme === 'LIGHT' ? 'light-theme' : ''}`}>
                        <PhotoCamera />
                    </label>
                    <input style={{ display: "none" }} type="file" id={fileInputId} accept="image/*"
                           onChange={handleFileChange}
                           ref={fileInputRef} />

                    <button className={`${styles.giphyButton} ${theme === 'LIGHT' ? 'light-theme' : ''}`}
                            onClick={() => setIsGiphyModalOpen(true)}>
                        <Gif />
                    </button>
                </div>
            )} */}

            {/* {(selectedGif || imagePreview) && (
                <div className={styles.selectedImage}
                     data-postcomment-instance={parentCommentId ? `reply-${parentCommentId}` : `main-${postId}`}>
                    {selectedGif && (
                        <>
                            <button className={styles.removeBtn} onClick={handleRemoveSelection}>
                                <CloseIcon />
                            </button>
                            <img src={selectedGif.images.fixed_height.url} className={styles.selectedRawimage}
                                 alt="Selected Gif" />
                        </>
                    )}
                    {imagePreview && (
                        <>
                            <button className={styles.removeBtn} onClick={handleRemoveSelection}>
                                <CloseIcon />
                            </button>
                            <img src={imagePreview} className={styles.selectedRawimage} alt="Selected Image" />
                        </>
                    )}
                </div>
            )} */}

            <button className={`${styles.submit} send`} onClick={() => { postCommentHandler(postId) }}>
                <Translate textKey="post" />
            </button>

            {isGiphyModalOpen && (
                <div className="customModal">
                    <div className="modalContent">
                        <button className="closeButton" onClick={() => setIsGiphyModalOpen(false)}>
                            &times;
                        </button>
                        <GiphySearchbox
                            apiKey={gifApiKey}
                            onSelect={onGifSelect}
                            searchPlaceholder={translateText("search")}
                            masonryConfig={[
                                { columns: 2, imageWidth: 110, gutter: 5 },
                                { mq: '700px', columns: 3, imageWidth: 120, gutter: 5 }
                            ]}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default PostComment;